import { useState, useMemo, useCallback } from 'react';
import { JournalEntry } from '../types/accounting';
import { useBusinessUnit } from './useBusinessUnit';
import { sumMoney, moneyEqual } from '../utils/currency';

export interface EnhancedJournalFilters {
  search: string; // Conservé pour compatibilité mais géré par DataTable
  journalType: string;
  startDate: string;
  endDate: string;
  status: string;
  source: string;
  businessUnitId?: string; // Filtre par unité commerciale
}

const initialFilters: EnhancedJournalFilters = {
  search: '',
  journalType: '',
  startDate: '',
  endDate: '',
  status: '',
  source: '',
  businessUnitId: undefined
};

/**
 * Hook pour les filtres métier avancés des écritures comptables
 * Note: La recherche textuelle est désormais gérée par DataTable
 * Ce hook se concentre sur les filtres métier spécifiques
 */
export function useEnhancedJournalFilters(entries: JournalEntry[]) {
  const [filters, setFilters] = useState<EnhancedJournalFilters>(initialFilters);
  
  // Intégration du filtre par unité commerciale
  const { getSelectedUnitIds, belongsToSelectedUnit, selectedUnit } = useBusinessUnit();

  const updateFilters = useCallback((newFilters: Partial<EnhancedJournalFilters>) => {
    setFilters(prev => ({
      ...prev,
      ...newFilters
    }));
  }, []);

  const resetFilters = useCallback(() => {
    setFilters(initialFilters);
  }, []);

  // S'assurer que entries est toujours un tableau
  const safeEntries = Array.isArray(entries) ? entries : [];

  const filteredEntries = useMemo(() => {
    if (!safeEntries || safeEntries.length === 0) return [];

    return safeEntries.filter(entry => {
      // Filtre par unité commerciale (entreprise/succursale/point de vente)
      // Appliqué automatiquement via le store businessUnit
      if (selectedUnit && entry.businessUnitId) {
        if (!belongsToSelectedUnit(entry.businessUnitId)) return false;
      }
      
      // Note: La recherche textuelle est maintenant gérée par DataTable (enableGlobalFilter)
      // Le champ search est conservé pour compatibilité mais n'est plus filtré ici

      // Filtre par type de journal
      if (filters.journalType && filters.journalType !== '' && filters.journalType !== 'all') {
        if (entry.journalType !== filters.journalType) return false;
      }

      // Filtre par statut
      if (filters.status && filters.status !== '' && filters.status !== 'all') {
        if (entry.status !== filters.status) return false;
      }

      // Filtre par source
      if (filters.source && filters.source !== '' && filters.source !== 'all') {
        const entrySource = entry.source || 'manual'; // Default to manual if undefined
        if (entrySource !== filters.source) return false;
      }

      // Filtre par date de début
      if (filters.startDate) {
        const entryDate = new Date(entry.date);
        const startDate = new Date(filters.startDate);
        if (entryDate < startDate) return false;
      }

      // Filtre par date de fin
      if (filters.endDate) {
        const entryDate = new Date(entry.date);
        const endDate = new Date(filters.endDate);
        if (entryDate > endDate) return false;
      }

      return true;
    });
  }, [safeEntries, filters, selectedUnit, belongsToSelectedUnit]);

  const isFiltersActive = useMemo(() => {
    return !!(
      filters.search || 
      (filters.journalType && filters.journalType !== '' && filters.journalType !== 'all') || 
      filters.startDate || 
      filters.endDate || 
      (filters.status && filters.status !== '' && filters.status !== 'all') || 
      (filters.source && filters.source !== '' && filters.source !== 'all')
    );
  }, [filters]);

  const filterStats = useMemo(() => {
    const totalEntries = safeEntries.length;
    const filteredCount = filteredEntries.length;
    const totalDebit = sumMoney(filteredEntries.map(entry => entry.totalDebit || 0));
    const totalCredit = sumMoney(filteredEntries.map(entry => entry.totalCredit || 0));

    return {
      totalEntries,
      filteredCount,
      filteredPercentage: totalEntries > 0 ? Math.round((filteredCount / totalEntries) * 100) : 0,
      totalDebit,
      totalCredit,
      isBalanced: moneyEqual(totalDebit, totalCredit)
    };
  }, [safeEntries, filteredEntries]);

  // Fonctions utilitaires pour les options de filtre
  const getJournalTypeOptions = useMemo(() => {
    return [
      { value: '', label: 'Tous les types' },
      { value: 'sales', label: 'Ventes' },
      { value: 'purchases', label: 'Achats' },
      { value: 'bank', label: 'Banque' },
      { value: 'cash', label: 'Caisse' },
      { value: 'general', label: 'OD Diverses' }
    ];
  }, []);

  const getStatusOptions = useMemo(() => {
    return [
      { value: '', label: 'Tous les statuts' },
      { value: 'draft', label: 'Brouillon' },
      { value: 'pending', label: 'En attente' },
      { value: 'approved', label: 'Approuvé' },
      { value: 'posted', label: 'Comptabilisé' }
    ];
  }, []);

  const getSourceOptions = useMemo(() => {
    return [
      { value: '', label: 'Toutes les sources' },
      { value: 'manual', label: 'Saisie manuelle' },
      { value: 'agent', label: 'ADHA AI' },
      { value: 'import', label: 'Import' }
    ];
  }, []);

  return {
    filters,
    updateFilters,
    resetFilters,
    filteredEntries,
    isFiltersActive,
    filterStats,
    getJournalTypeOptions,
    getStatusOptions,
    getSourceOptions
  };
}
